import React, {useEffect, useState} from "react";
import MagnifyingGlassIcon from "@heroicons/react/20/solid/MagnifyingGlassIcon";
import {FieldClearIcon} from "../../../../data/themes/icons";
import {classNames} from "../../../util/util-helpers";
import {scrollIntoView} from "../../../util/util-vanilla";

export default function FieldSearch({
                                        id,
                                        innerRef,
                                        type,
                                        onChange,
                                        readOnly,
                                        autoFocus,
                                        onBlur,
                                        onFocus,
                                        onKeyDown,
                                        name,
                                        value = "",
                                        placeholder,
                                        disabled,
                                        errorMessage,
                                        min,
                                        max,
                                        step,
                                        autocomplete,
                                        addClass,
                                        translate,
                                        icon
                                    }) {
    const [inputValue, setInputValue] = useState(value);
    const [hintMessage, setHintMessage] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const hasIcon = icon !== false;

    const onSearchChange = (event) => {
        setInputValue(event.target.value);
    }

    useEffect(() => {
        if (!value && !!inputValue) {
            setInputValue("");
        } else if (value && !inputValue) {
            setInputValue(value);
        }
    }, [value])

    useEffect(() => {
        let debounceTimeout;
        let hintTimeout;
        const len = inputValue?.length ?? 0;
        const shouldFire = len >= 3 || len === 0;

        if (inputValue !== undefined && value !== inputValue && shouldFire) {
            debounceTimeout = setTimeout(() => onChange(name, inputValue), 500);
        }

        if (len >= 1 && len <= 2) {
            // value being non-empty means a prior 3+ char search was committed to the parent
            if (value) {
                setHintMessage("Results are from your previous search. Type 3+ characters to search again.");
            } else {
                hintTimeout = setTimeout(
                    () => setHintMessage("Type at least 3 characters to search"),
                    2000
                );
            }
        } else {
            setHintMessage("");
        }

        if (inputValue === undefined && value) {
            setInputValue(value);
        }

        return () => {
            clearTimeout(debounceTimeout);
            clearTimeout(hintTimeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue])

    const input = (
        <input
            id={id}
            ref={innerRef}
            type={type}
            readOnly={readOnly}
            autoFocus={autoFocus}
            onChange={onSearchChange}
            onBlur={(event) => {
                setIsFocused(false);

                if (onBlur) {
                    onBlur(event);
                }
            }}
            onFocus={(event) => {
                setIsFocused(true);

                if (onFocus) {
                    onFocus(event);
                }

                scrollIntoView(event);
            }}
            onKeyDown={onKeyDown}
            name={name}
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            className={classNames("py-1", hasIcon ? "pl-9" : undefined, addClass, (errorMessage ? " is-invalid" : ""))}
            min={min}
            maxLength={max}
            step={step}
            autoComplete={autocomplete ?? "off"}
        />);

    return (
        <React.Fragment>
            <div className="relative">
                {hasIcon && (
                    <div
                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="w-5 h-5 text-tm-gray-400 z-10"/>
                    </div>
                )}

                {!!value && (
                    <button
                        onClick={() => onChange(name, "")}
                        className="absolute z-10 inset-y-0 right-0 px-3 flex items-center cursor-pointer text-tm-gray-900 hover:text-tm-gray-400">
                        <FieldClearIcon/>
                    </button>
                )}

                {input}

                {isFocused && !!hintMessage && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 pointer-events-none bg-popup border border-tm-gray-300 rounded-lg shadow-lg py-2 px-3 text-sm text-tm-gray-700">
                        {hintMessage}
                    </div>
                )}
            </div>

            {errorMessage && (
                <div className={"text-red-600 font-semibold"}>
                    {!!translate && (
                        errorMessage.reduce((memo, it) => {
                            return translate(it) + (memo ? "," + memo : "")
                        }, "")
                    )}
                    {!translate && (
                        <span>Field required*</span>
                    )}
                </div>
            )}
        </React.Fragment>

    )
}
